import { FC, useEffect, useRef, useState } from "react";
import Label from "../Label/Label";
import { fetchBase64Image } from "../../../../utils/helpers/fetchBase64profilePhoto";
import Icon from "../../../Icon/Icon";
import dayjs from "dayjs";
import ImageEditor from "../ImageEditor/ImageEditor";
import { createPortal } from "react-dom";
import { H1, P, Span } from "../../../Text/Text";
import { getUploadedImageFromFileSize } from "../../helpers";
import usePopup from "../../../../utils/hooks/usePopup";
import Button from "../../../Button/Button";

import styles from "./selectImage.module.scss";

type ImageFieldPropTypes = {
  name: string;
  label: string;
  register: any;
  orderIndex: number;
  errorMessage: string;
  style: any;
  src: any;
  onChange: (value: any) => void;
  extraContent: any;
  hintText?: string;
  fileName?: string;
  imageType?: string;
  minEdgeLength?: number;
  allowKeepOriginalRatio?: boolean;
  /** When true, only square images are accepted (e.g. clock logo). */
  requireSquare?: boolean;
};

const MIN_IMG_EDGE_LENGTH = 200;

const ImageField: FC<ImageFieldPropTypes> = (props) => {
  const {
    name,
    label,
    orderIndex,
    style,
    onChange,
    src,
    extraContent,
    hintText,
    fileName,
    imageType,
    minEdgeLength = MIN_IMG_EDGE_LENGTH,
    allowKeepOriginalRatio = false,
    requireSquare = false
  } = props;

  const [file, setFile] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileBeforeEditRef = useRef<any>(null);
  const { createPopup, removePopup } = usePopup();

  useEffect(() => {
    if (src) {
      let url = src;

      if (!src.includes("https") && window.location.protocol === "https:") {
        url = url.replace("http", "https");
      }

      fetchBase64Image(url).then((res) => {
        setFile(res);
      });
    }
  }, [src]);

  const handleRemove = () => {
    onChange(null);
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleCancelEdit = () => {
    setShowEditor(false);
    setFile(fileBeforeEditRef.current ?? null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const saveTransformedFile = async (fileData: any) => {
    setFile(fileData);

    const result = await fetch(fileData);
    const blob = await result.blob();
    const file = new File([blob], fileName || "image.jpeg", { type: imageType || "image/jpeg" });
    setShowEditor(false);
    const ImageObjectURL = URL.createObjectURL(blob);

    await fetchBase64Image(ImageObjectURL).then((ImageSource) => {
      onChange({
        ImageFile: {
          lastModified: file?.lastModified,
          lastModifiedDate: dayjs().toISOString(),
          name: file?.name,
          size: file?.size,
          type: file?.type,
          webkitRelativePath: "",
        },
        ImageObjectURL,
        ImageSource,
      });
    });
  };

  const handleChange = async (e) => {
    const uploadedImage: File = e.target.files[0];
    if (!uploadedImage) {
      return;
    }

    if (
      (imageType && uploadedImage.type !== imageType) ||
      (uploadedImage.type !== "image/jpeg" &&
        uploadedImage.type !== "image/jpg" &&
        uploadedImage.type !== "image/png" &&
        uploadedImage.type !== "image/gif")
    ) {
      const popup = {
        header: <H1>Error</H1>,
        body: <P>Wrong file format. Choose another one, please.</P>,
        footer: (
          <>
            <Button onClick={removePopup}>Close</Button>
          </>
        ),
        className: styles.errorPopup
      };

      createPopup(popup);

      return;
    }

    const [width, height] = await getUploadedImageFromFileSize(uploadedImage);

    const meetsMinSize = width >= minEdgeLength && height >= minEdgeLength;
    const isSquare = !requireSquare || Math.abs(width - height) < 2;

    if (meetsMinSize && isSquare) {
      fileBeforeEditRef.current = file;
      setFile(uploadedImage);
      setShowEditor(true);
    } else {
      e.target.files = null;

      const message = !meetsMinSize
        ? hintText
        : "Image must be square (same width and height).";

      const popup = {
        header: <H1>Error</H1>,
        body: <P>{message}</P>,
        footer: <Button onClick={removePopup}>Close</Button>,
        className: styles.errorPopup,
      };

      createPopup(popup);
    }
  };

  return (
    <div key={name} style={{ order: orderIndex, ...style }}>
      <Label name={name} label={label} hintText={hintText}>
        <div
          className={styles.wrapper}
          style={{
            backgroundImage: `url(${file})`,
          }}
        >
          {showEditor &&
            createPortal(
              <ImageEditor
                file={file}
                onSave={saveTransformedFile}
                setShowEditor={setShowEditor}
                onCancel={handleCancelEdit}
                allowKeepOriginalRatio={allowKeepOriginalRatio}
              />,
              document.body
            )}
          <Span>Browse file to upload</Span>
          <input
            ref={inputRef}
            type="file"
            name={name}
            accept="image/png, image/gif, image/jpeg"
            onChange={handleChange}
          />
        </div>
        <Icon
          name="bin"
          onClick={handleRemove}
          className={`${styles.removeButton} ImageField-RemoveButton`}
        />
      </Label>
      {!!extraContent && extraContent()}
    </div>
  );
};

export default ImageField;
